import React from 'react';

/**
 * SkeletonLoader - Reusable skeleton loading components
 */

// Basic skeleton shapes
export function SkeletonText({ width = '100%', className = '' }) {
  return (
    <div
      className={`skeleton skeleton-text ${className}`}
      style={{ width }}
    />
  );
}

export function SkeletonTitle({ width = '40%' }) {
  return <div className="skeleton skeleton-title" style={{ width }} />;
}

export function SkeletonAvatar({ size = 48 }) {
  return (
    <div
      className="skeleton skeleton-avatar"
      style={{ width: size, height: size }}
    />
  );
}

export function SkeletonButton({ width = 120 }) {
  return <div className="skeleton skeleton-btn" style={{ width }} />;
}

// Composite skeleton layouts
export function SkeletonCard({ children }) {
  return <div className="skeleton-card">{children}</div>;
}

export function SkeletonTableRow({ columns = 4 }) {
  return (
    <div className="skeleton-row">
      {Array.from({ length: columns }).map((_, i) => (
        <div
          key={i}
          className="skeleton skeleton-cell"
          style={{ flex: i === 0 ? 2 : 1 }}
        />
      ))}
    </div>
  );
}

// Pre-built skeleton patterns
export function SkeletonDashboardStats({ count = 3 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, 1fr)`, gap: '20px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i}>
          <SkeletonText width="60%" />
          <div style={{ height: 8 }} />
          <div className="skeleton" style={{ height: 40, width: '50%' }} />
        </SkeletonCard>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, columns = 4 }) {
  return (
    <div className="skeleton-card" style={{ padding: 0 }}>
      <div style={{ padding: '15px 20px', borderBottom: '2px solid var(--color-border)' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          {Array.from({ length: columns }).map((_, i) => (
            <div
              key={i}
              className="skeleton"
              style={{ height: 16, flex: i === 0 ? 2 : 1 }}
            />
          ))}
        </div>
      </div>
      <div style={{ padding: '0 20px' }}>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </div>
    </div>
  );
}

export function SkeletonForm({ fields = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i}>
          <SkeletonText width="30%" className="skeleton-text-sm" />
          <div style={{ height: 8 }} />
          <div className="skeleton" style={{ height: 44, width: '100%' }} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
        <SkeletonButton width={100} />
        <SkeletonButton width={120} />
      </div>
    </div>
  );
}

export function SkeletonHubCards({ count = 4 }) {
  return (
    <div className="admin-hub-grid">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i}>
          <div className="skeleton" style={{ width: 48, height: 48, borderRadius: 8, marginBottom: 15 }} />
          <SkeletonText width="60%" />
          <div style={{ height: 8 }} />
          <SkeletonText width="90%" className="skeleton-text-sm" />
          <SkeletonText width="70%" className="skeleton-text-sm" />
          <div style={{ height: 16 }} />
          <SkeletonButton width="100%" />
        </SkeletonCard>
      ))}
    </div>
  );
}

export function SkeletonListItem() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--color-border)' }}>
      <SkeletonAvatar size={40} />
      <div style={{ flex: 1 }}>
        <SkeletonText width="40%" />
        <div style={{ height: 4 }} />
        <SkeletonText width="60%" className="skeleton-text-sm" />
      </div>
      <SkeletonButton width={80} />
    </div>
  );
}

// Default export with all components
const SkeletonLoader = {
  Text: SkeletonText,
  Title: SkeletonTitle,
  Avatar: SkeletonAvatar,
  Button: SkeletonButton,
  Card: SkeletonCard,
  TableRow: SkeletonTableRow,
  DashboardStats: SkeletonDashboardStats,
  Table: SkeletonTable,
  Form: SkeletonForm,
  HubCards: SkeletonHubCards,
  ListItem: SkeletonListItem
};

export default SkeletonLoader;
